/**
 * IssueCard - Compact card for the Issues sidebar.
 *
 * Displays indication type, severity, asset path, impact, and state.
 * Used inside the Tasks Panel issues list.
 */

import type { Issue } from '@/types/tasks';
import { SEVERITY_COLORS, STATE_COLORS } from '@/types/tasks';
import { INDICATION_TYPES } from '../../stores';

interface IssueCardProps {
  issue: Issue;
  isSelected: boolean;
  onClick: () => void;
}

/** Build a readable asset location string from the asset path. */
function assetLocation(issue: Issue): string {
  const p = issue.assetPath;
  const parts: string[] = [];
  if (p.station) parts.push(`Sta: ${p.station}`);
  if (p.inverter) parts.push(`Inv: ${p.inverter}`);
  if (p.mppt) parts.push(`MPPT: ${p.mppt}`);
  if (p.dccb) parts.push(`DCCB: ${p.dccb}`);
  if (p.string) parts.push(`Str: ${p.string}`);
  if (p.module) parts.push(`Mod: ${p.module}`);
  return parts.join(' > ') || '—';
}

/** Format dollars compactly. */
function fmtDollars(v: number): string {
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
}

export default function IssueCard({ issue, isSelected, onClick }: IssueCardProps) {
  const sevColor = SEVERITY_COLORS[issue.severity];
  const stateColor = STATE_COLORS[issue.state];
  const typeConfig = INDICATION_TYPES[issue.indType as keyof typeof INDICATION_TYPES];
  const typeColor = typeConfig?.color ?? '#888';
  const typeLabel = typeConfig?.label ?? issue.indType;
  const impact = issue.estimatedImpact.revenueLoss;

  return (
    <div
      className={`tp-issue-card${isSelected ? ' tp-issue-card--selected' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    >
      {/* Row 1: Type badge + Severity badge */}
      <div className="tp-issue-card__header">
        <span
          className="tp-badge"
          style={{ background: typeColor, color: '#fff' }}
        >
          {typeLabel}
        </span>
        <span
          className="tp-badge"
          style={{ background: sevColor, color: '#fff' }}
        >
          {issue.severity}
        </span>
      </div>

      {/* Row 2: Asset location */}
      <div className="tp-issue-card__location">{assetLocation(issue)}</div>

      {/* Row 3: Impact + State */}
      <div className="tp-issue-card__footer">
        {impact > 0 && (
          <span className="tp-issue-card__impact">{fmtDollars(impact)}/yr</span>
        )}
        <span
          className="tp-badge tp-badge--outline"
          style={{ borderColor: stateColor, color: stateColor }}
        >
          {issue.state}
        </span>
      </div>
    </div>
  );
}
